import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClipboardList, faCheckCircle, faHourglass, faBan,
    faTruck, faFileInvoice, faFileAlt, faWeightHanging,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const StatCard = ({ label, value, sub, icon, color, bg, border }) => {
    const { t } = useTranslation();
    return (
    <div
        style={{
            flex: '1 1 180px', minWidth: 170, maxWidth: 260,
            backgroundColor: '#fff',
            border: `1px solid ${border}`,
            borderRadius: 14,
            padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
            transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 4px 16px ${border}bb`)}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </span>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                backgroundColor: bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color, fontSize: 13, flexShrink: 0, marginLeft: 8,
            }}>
                <FontAwesomeIcon icon={icon} />
            </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{value ?? t('common.symbols.emDash')}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
    );
};

const SectionBlock = ({ title, accent, cards }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: accent }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
    </div>
);

const SalesSummaryCards = ({ summary }) => {
    const { t } = useTranslation();

    const fmt = (n) => (n != null ? Number(n).toLocaleString() : t('common.symbols.emDash'));
    const fmtWt = (kg, tons) => {
        if (kg == null && tons == null) return t('common.symbols.emDash');
        return `${Number(tons ?? 0).toFixed(2)} T / ${Number(kg ?? 0).toLocaleString()} kg`;
    };

    if (!summary) return null;

    const strings = t('salesAnalytics.summary', { returnObjects: true });
    const s = summary;

    const overviewCards = [
        {
            label: strings.cards.totalOrders,
            value: fmt(s.total_orders),
            sub: strings.subs.allOrdersInPeriod,
            icon: faClipboardList,
            color: '#16a34a', bg: '#dcfce7', border: '#86efac',
        },
        {
            label: strings.cards.totalWeight,
            value: fmtWt(s.total_weight_kg, s.total_weight_tons),
            sub: strings.subs.tonsKgDispatched,
            icon: faWeightHanging,
            color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        },
    ];

    const statusCards = [
        {
            label: strings.cards.confirmed,
            value: fmt(s.confirmed),
            sub: strings.subs.confirmedOrders,
            icon: faCheckCircle,
            color: '#16a34a', bg: '#dcfce7', border: '#86efac',
        },
        {
            label: strings.cards.pending,
            value: fmt(s.pending),
            sub: strings.subs.awaitingConfirmation,
            icon: faHourglass,
            color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        },
        ...(s.cancelled != null ? [{
            label: strings.cards.cancelled,
            value: fmt(s.cancelled),
            sub: strings.subs.cancelledOrders,
            icon: faBan,
            color: '#ef4444', bg: '#fef2f2', border: '#fecaca',
        }] : []),
    ];

    const fulfillmentCards = [
        {
            label: strings.cards.delivered,
            value: fmt(s.delivered),
            sub: strings.subs.ordersDelivered,
            icon: faTruck,
            color: '#16a34a', bg: '#dcfce7', border: '#86efac',
        },
        {
            label: strings.cards.invoiced,
            value: fmt(s.invoiced),
            sub: strings.subs.ordersInvoiced,
            icon: faFileInvoice,
            color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        },
        {
            label: strings.cards.challanGenerated,
            value: fmt(s.challan_generated),
            sub: strings.subs.challansCreated,
            icon: faFileAlt,
            color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
        },
    ];

    const hasFulfillment = s.delivered != null || s.invoiced != null || s.challan_generated != null;

    return (
        <div>
            <SectionBlock title={strings.sections.overview} accent="#16a34a" cards={overviewCards} />
            <SectionBlock title={strings.sections.byStatus} accent="#7c3aed" cards={statusCards} />
            {hasFulfillment && <SectionBlock title={strings.sections.fulfillment} accent="#0891b2" cards={fulfillmentCards} />}
        </div>
    );
};

export default SalesSummaryCards;
